import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useState } from "react";
import type { ConfirmationModalProps } from "./confirmation";
import { ConfirmationModal } from "./confirmation";

const ConfirmationContext = createContext<(options?: ConfirmationModalProps["options"]) => Promise<boolean>>(
  Promise.reject
);

// eslint-disable-next-line react-refresh/only-export-components
export const useConfirmation = () => useContext(ConfirmationContext);

export const ConfirmationServiceProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState<boolean>(false);

  const [awaitingPromise, setAwaitingPromise] = useState<{
    reject: () => void;
    resolve: (confirmed: boolean) => void;
  }>();

  const [options, setOptions] = useState<ConfirmationModalProps["options"]>();

  const openConfirmation = useCallback(
    (options?: ConfirmationModalProps["options"]) => {
      setOpen(true);
      setOptions(options);
      return new Promise<boolean>((resolve, reject) => {
        setAwaitingPromise({ reject, resolve });
      });
    },
    [setAwaitingPromise, setOpen]
  );

  const handleClose = () => {
    if (awaitingPromise) {
      awaitingPromise.resolve(false);
    }

    setOpen(false);
  };

  const handleSubmit = () => {
    if (awaitingPromise) {
      awaitingPromise.resolve(true);
    }

    setOpen(false);
  };

  return (
    <>
      <ConfirmationContext.Provider value={openConfirmation}>{children}</ConfirmationContext.Provider>
      {open && <ConfirmationModal isOpen={open} options={options} onClose={handleClose} onConfirm={handleSubmit} />}
    </>
  );
};
